import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedButtonPrimeReact,
	EnhancedDialog,
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	ITest,
	LabelPosition,
	RHookFormTextFieldWithLabel,
	SingleColumnLayout,
	StandardPanel,
	StandardSubmitFormButtonsToolbar,
} from "@websential/cosmic-v20";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
	YEAR_SCREEN_SLUG,
} from "../../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../../DDLDataTable";
import { IAppState } from "../../../store/reducers";
import { IGetListRequest } from "../../../models";
import { ITaxYear } from "../../../store/reducers/taxYearReducers";
import { IYearRequestPayload } from "../../../models/Year";
import { TaxYearSetupToolbar } from "./TaxYearSetupToolbar";
import { YearSchema } from "./validator";
import { YearService } from "../../../services/YearService";
import { instanceOfIYearsResponce } from "../../../common/instance-method";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigateToLogout } from "../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IYearSetup extends ITest { }
const YEAR_LIST_QUERY_KEY: any = "years-list";

const useYears = (
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["years-list", params],
		queryFn: (data) => {
			return YearService.getList(params, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});
// for edit
const YEARS_DATA_QUERY_KEY: any = "get-years-data";

const useGetData = (id: string | undefined) => {
	return useQuery({
		queryKey: [YEARS_DATA_QUERY_KEY, id],
		queryFn: () => YearService.getById(id),
		refetchOnWindowFocus: false,
	});
};

const DEFAULT_VALUES = {

	taxYear: new Date().getFullYear(),
	status: 100
};

export const YearSetup: FC<IYearSetup> = ({ testId }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const navigateToLogout = useNavigateToLogout();

	// for edit
	const queryClient = useQueryClient();
	const { id } = useParams();
	const theme = useTheme();

	const [editRecordId, setEditRecordId] = useState(id);

	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [taxYearId, setTaxYearId] = useState<any>(0);

	const { isPending: isSubmitting, mutate: submitFormData } = useMutation({
		mutationFn: YearService.create,
		onSuccess: (res: IYearRequestPayload | IServerError | IResponse | any) => {

			if (instanceOfIYearsResponce(res)) {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
				// return queryClient.invalidateQueries({
				// 	queryKey: [YEARS_DATA_QUERY_KEY],
				// });
				let path = `/tax-year/setup/${res.id}`;
				navigate(path);
				return Promise.all([
					queryClient.invalidateQueries({
						queryKey: [YEAR_LIST_QUERY_KEY],
					}),
					queryClient.invalidateQueries({
						queryKey: [YEARS_DATA_QUERY_KEY],
					}),
				]);
			}
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "An error occurred, please try again",
				severity: "error",
			});
		},
	});

	// for edit
	const { isPending: isPendingData, data: selectedYearData } =
		useGetData(editRecordId);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "taxYear",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "taxYear",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useYears(queryParams, navigateToLogout); //useQuery('accountants-list', AccountantService.getAccountantList, { refetchOnWindowFocus: false });
	const taxYearList: any = data;

	const upadtedTaxYearListItems = useMemo(() => {
		return (
			taxYearList &&
			taxYearList.items &&
			taxYearList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/tax-year/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.taxYear.toString()}
					</Link>
				),
			}))
		);
	}, [taxYearList]);

	const formMethods = useForm<any>({
		resolver: yupResolver(YearSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES,
	});

	const onSubmit = (formData: IYearRequestPayload) => {

		let payload = {
			id: id ? id : undefined,
			taxYear: formData.taxYear,
			status: formData.status,
		};
		submitFormData(payload);
	};

	const { isPending: isDeleting, mutate: deleteTaxYearSetupById } = useMutation(
		{
			mutationFn: YearService.delYear,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the TaxYearSetup fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/tax-year/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [YEAR_LIST_QUERY_KEY],
				});
			},
			onError: (err) => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		}
	);

	const deleteTaxYearSetupField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload: any = {
				taxYearId: taxYearId,
				navigateToLogout: navigateToLogout,
			};
			deleteTaxYearSetupById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedYearData) {
			setTaxYearId(selectedYearData?.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setTaxYearId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveTaxYearById } = useMutation({
		mutationFn: YearService.archiveTaxYear,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the TaxYear fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/tax-year/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [YEAR_LIST_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveTaxYearField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				taxYearId: taxYearId,
				navigateToLogout: navigateToLogout,
			};
			archiveTaxYearById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedYearData) {
			setTaxYearId(selectedYearData.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onRowEditClicked = useCallback((params: ITaxYear | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/tax-year/setup/${params.value}`;
		navigate(path);
	}, []);

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset(DEFAULT_VALUES);
		let path = `/tax-year/setup`;
		navigate(path);
	};

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	// for edit
	useEffect(() => {
		// if the selected flex field data has loaded.
		if (selectedYearData) {
			Object.keys(selectedYearData).forEach((fieldName) => {

				// @ts-ignore
				const fieldValue = selectedYearData[fieldName];

				// @ts-ignore
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		} else {
			formMethods.setValue("taxYear", DEFAULT_VALUES.taxYear);
		}
	}, [selectedYearData, formMethods.setValue, formMethods]);

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Misc.", "Tax Year"];
	const expandedTaxYearSetupItem = "Tax Year";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Misc." && item.label === expandedTaxYearSetupItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedTaxYearSetupItem && "/tax-year/setup",
			};
			return enhancedItem;
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: YEAR_SCREEN_SLUG,
				companyName: "",
				activityType: "activity_type=create&activity_type=updated",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
			size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
		// sx={{ "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingData || isDeleting || isArchive}
				testId={`${testId}-backdrop`}
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteTaxYearSetupField}
				cancelActionHandler={onCloseHandler}
				testId="accountant-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title=" Year"
										testId={`${testId}-taxslip-year`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<RHookFormTextFieldWithLabel
											id="taxYear"
											testId={`${testId}-year`}
											label="Year"
											labelPosition={LabelPosition.Top}
											required
										/>
										<EnhancedDropDownRHFWithLabel
											items={[
												{ label: "Active", value: 100 },
												{ label: "In-Active", value: 200 }
											]} testId={'status'} selectName={"status"} label={"Status"}
											selectedItem={100}
											labelPosition={LabelPosition.Top} />

									</StandardPanel>
								}
								toolbar={
									<TaxYearSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="bottom"
							/>
						</Grid>
						{/* <Grid size={{ xs: 0, sm: 0, md: 5, lg: 5, xl: 5 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }} // old */}
						<Grid size={{ xs: 0, sm: 0, md: 5.4, lg: 5.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 3.1, lg: 3.1, xl: 3.1 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									taxYearList && upadtedTaxYearListItems
										? upadtedTaxYearListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									taxYearList && taxYearList.total ? taxYearList.total : 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={taxYearList && id && taxYearList.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveTaxYearField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`${testId}-snackbar`}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
